import { PropsWithChildren, useEffect, useState } from 'react';
import { I18nProvider, I18nProviderProps } from '@amzn/awsui-components-react/polaris/i18n';
import cloudscapeEnglish from '@amzn/awsui-components-react/polaris/i18n/messages/all.en';
import { useLocalizationContext } from '@amzn/react-arb-tools';
import { DEFAULT_LOCALE } from '../../data/supportedLanguages';

/** Where the deployed site serves the reassembled Cloudscape catalogs from. */
export const CLOUDSCAPE_MESSAGES_URL_PREFIX = '/cloudscape';

const CLOUDSCAPE_NAMESPACE = 'cloudscape-design-components';
const DEFAULT_LANGUAGE = languageOf(DEFAULT_LOCALE);

function languageOf(locale: string) {
  return locale.split('-')[0];
}

function holdsLanguage(payload: unknown, language: string) {
  const catalog = (payload as Record<string, Record<string, unknown>> | undefined)?.[
    CLOUDSCAPE_NAMESPACE
  ]?.[language];
  return typeof catalog === 'object' && catalog !== null && Object.keys(catalog).length > 0;
}

function reportUnavailable(url: string) {
  console.error(`Cloudscape text stays in English: ${url} could not be used.`);
}

type LoadedMessages = {
  language: string;
  messages: I18nProviderProps.Messages;
};

/**
 * Supplies the translations for text that Cloudscape renders on its own, such as the buttons of a
 * wizard or the controls of a table.
 *
 * English is built in and stays in effect until a usable catalog for the session's language has
 * arrived, because a half-applied language would leave those labels blank instead.
 */
// TODO: cce-7183 - Components still pass these strings as props, which take priority over this.
export function CloudscapeI18nProvider({ children }: PropsWithChildren) {
  const { localizationContext } = useLocalizationContext();
  const [loaded, setLoaded] = useState<LoadedMessages>();

  const locale = localizationContext.getLocale();
  const language = languageOf(locale);
  const isEnglish = language === DEFAULT_LANGUAGE;
  const ready = isEnglish || loaded?.language === language;

  useEffect(() => {
    if (isEnglish) {
      return;
    }
    let abandoned = false;
    const url = `${CLOUDSCAPE_MESSAGES_URL_PREFIX}/messages-${language}.json`;

    fetch(url)
      .then((response) => (response.ok ? response.json() : undefined))
      .then((payload) => {
        if (abandoned) {
          return;
        }
        if (holdsLanguage(payload, language)) {
          setLoaded({ language, messages: payload as I18nProviderProps.Messages });
        } else {
          reportUnavailable(url);
        }
      })
      .catch(() => {
        if (!abandoned) {
          reportUnavailable(url);
        }
      });

    return () => {
      abandoned = true;
    };
  }, [isEnglish, language]);

  return (
    <I18nProvider
      locale={ready ? locale : DEFAULT_LOCALE}
      messages={loaded && ready ? [cloudscapeEnglish, loaded.messages] : [cloudscapeEnglish]}
    >
      {children}
    </I18nProvider>
  );
}
